import { useCallback, useMemo } from 'react'

import { FilterIcon } from 'lucide-react'
import { Button } from 'twyne-new.ui'

import { createFilterItem, getAvailableFilterEntities } from '../utils/filterEntities'

import FilterByDropdown from './FilterByDropdown'

export function useFilterEditorState(filters = [], onChange) {
  const activeEntities = useMemo(
    () => filters.map(filter => filter.entity).filter(Boolean),
    [filters],
  )

  const hasPendingFilter = useMemo(
    () => filters.some(filter => !filter.entity),
    [filters],
  )

  const handleAddFilter = useCallback(() => {
    onChange([...filters, createFilterItem()])
  }, [filters, onChange])

  const handleUpdateFilter = useCallback((filterId, nextFilter) => {
    onChange(filters.map(filter => filter.id === filterId ? nextFilter : filter))
  }, [filters, onChange])

  const handleRemoveFilter = useCallback(filterId => {
    onChange(filters.filter(filter => filter.id !== filterId))
  }, [filters, onChange])

  return useMemo(() => ({
    activeEntities,
    hasPendingFilter,
    handleAddFilter,
    handleUpdateFilter,
    handleRemoveFilter,
  }), [
    activeEntities,
    hasPendingFilter,
    handleAddFilter,
    handleUpdateFilter,
    handleRemoveFilter,
  ])
}

export function AddFilterButton({
  collection,
  activeEntities = [],
  hasPendingFilter = false,
  onAdd,
}) {
  const availableEntities = useMemo(
    () => getAvailableFilterEntities(collection, activeEntities),
    [collection, activeEntities],
  )

  return (
    <Button
      size="md"
      variant="outline"
      className="gap-1.5 font-normal"
      disabled={hasPendingFilter || !availableEntities.length}
      onClick={onAdd}
    >
      <FilterIcon size={14} className="shrink-0" />
      Add filter
    </Button>
  )
}

export function FilterByDropdowns({
  collection,
  filters = [],
  activeEntities = [],
  onUpdateFilter,
  onRemoveFilter,
}) {
  return filters.map(filter => <FilterByDropdown
    key={filter.id}
    filter={filter}
    collection={collection}
    activeEntities={activeEntities}
    onChange={nextFilter => onUpdateFilter(filter.id, nextFilter)}
    onRemove={() => onRemoveFilter(filter.id)}
  />)
}
